import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FaEdit } from "react-icons/fa";
import { FiEye, FiSearch } from "react-icons/fi";
import {
  Box,
  Flex,
  Text,
  Button,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  useColorModeValue,
  Badge,
  Spinner,
  Alert,
  AlertIcon,
  Card,
  Input,
  InputGroup,
  InputRightElement,
} from "@chakra-ui/react";
import { fetchCreditData } from "../../redux/creditSlice";
import { useNavigate, useLocation } from "react-router-dom";
import TableView from "../../components/TableView";
import GasCylinderLoader from "../../components/GasCylinderLoader";

export default function Credit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { data, loading, error } = useSelector((state) => state.credit);

  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 0);
  const [rowsPerPage, setRowsPerPage] = useState(() => Number(localStorage.getItem("rowsPerPage")) || 10);
  const [currentPage, setCurrentPage] = useState(() => Number(localStorage.getItem("currentPage")) || 1);
  const [searchTerm, setSearchTerm] = useState("");
  const [initialValue, setInitialValue] = useState(1);

  const primaryColor = "#347433";

  useEffect(() => {
    dispatch(fetchCreditData({ page: 1, limit: 1000 }));
  }, [dispatch]);

  useEffect(() => {
    // 👇 If user came from sidebar, directly open the Approved Applications tab (index 1)
    if (location.state?.fromSidebar) {
      setActiveTab(1);
    }
  }, [location.state]);

  
  useEffect(() => {
    localStorage.setItem("rowsPerPage", rowsPerPage);
  }, [rowsPerPage]);

  useEffect(() => {
    localStorage.setItem("currentPage", currentPage);
  }, [currentPage]);

  const allApplications = data || [];

  // ✅ Filter applications based on search term (customer name)
  const filteredApplications = useMemo(() => {
    if (!searchTerm.trim()) return allApplications;
    return allApplications.filter((app) =>
      app.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm, allApplications]);

  // ---- STATUS FILTERS ----
  const underReviewApplications = filteredApplications.filter((app) => {
    const latest = app.details?.[app.details.length - 1];
    return latest && String(latest.status) === "0";
  });

  const rejectedApplications = filteredApplications.filter((app) => {
    const latest = app.details?.[app.details.length - 1];
    return latest && String(latest.status) === "1";
  });

  const commentedApplications = filteredApplications.filter((app) => {
    const latest = app.details?.[app.details.length - 1];
    return latest && String(latest.status) === "2";
  });

  const approvedApplications = filteredApplications.filter((app) => {
    const latest = app.details?.[app.details.length - 1];
    return latest && String(latest.status) === "3";
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case "0":
        return { color: "yellow", text: "Under Review" };
      case "1":
        return { color: "red", text: "Rejected" };
      case "2":
        return { color: "orange", text: "Commented" };
      case "3":
        return { color: "green", text: "Approved" };
      default:
        return { color: "gray", text: "Unknown" };
    }
  };

  const renderStatusTable = (applications, statusName) => {
    if (applications.length === 0) {
      return (
        <Box bg="#fff" p={6} textAlign="center">
          <Text fontSize="sm" color="gray.500">
            No data available
          </Text>
        </Box>
      );
    }

    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginated = applications.slice(startIndex, startIndex + rowsPerPage);

    return (
      <TableView
        headData={[
          "Application ID",
          "Customer Name",
          "Type",
          "Requested Limit",
          "Credit Days",
          "Status",
          "Mobile",
          "Actions",
        ]}
        body={paginated.map((app, idx) => {
          const latestStatus = app.details?.[app.details.length - 1]?.status || "0";
          const statusBadge = getStatusBadge(latestStatus);
          return (
            <Tr key={idx}>
              <Td textAlign="center">{app.application_id || `CR-${app.id}`}</Td>
              <Td textAlign="center">{app.customer_name || "N/A"}</Td>
              <Td textAlign="center">
                <Badge
                  colorScheme={app.customer_type === "1" ? "blue" : "purple"}
                  borderRadius="md"
                  px={2}
                >
                  {app.customer_type === "1" ? "B2B" : "B2C"}
                </Badge>
              </Td>
              <Td textAlign="center">
                ₹{parseInt(app.req_credit_limit || 0).toLocaleString("en-IN")}
              </Td>
              <Td textAlign="center">{app.req_credit_days} Days</Td>
              <Td textAlign="center">
                <Badge colorScheme={statusBadge.color} borderRadius="md" px={2}>
                  {statusBadge.text}
                </Badge>
              </Td>
              <Td textAlign="center">{app.mobile || "N/A"}</Td>

              <Td textAlign="center">
                <Flex gap={2} justify="center">
                  <Button
                    size="xs"
                    bg={primaryColor}
                    color="white"
                    onClick={() =>
                      navigate("credit-detail", {
                        state: { id: app.id, previousTab: activeTab, isViewOnly: false },
                      })
                    }
                  >
                    <FaEdit size={12} />
                  </Button>
                  <Button
                    size="xs"
                    bg="gray.500"
                    color="white"
                    onClick={() =>
                      navigate("credit-detail", {
                        state: { id: app.id, previousTab: activeTab, isViewOnly: true },
                      })
                    }
                  >
                    <FiEye />
                  </Button>
                </Flex>
              </Td>
            </Tr>
          );
        })}
        initialValue={startIndex + 1}
        page={currentPage}
        rows={rowsPerPage}
        total_value={applications.length}
        pagination
        onChange={(e) => {
          setRowsPerPage(Number(e.target.value));
          setCurrentPage(1);
          setInitialValue(1);
        }}
        onPrevious={() => {
          if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
            setInitialValue(initialValue - rowsPerPage);
          }
        }}
        onNext={() => {
          if (rowsPerPage * currentPage < applications.length) {
            setCurrentPage(currentPage + 1);
            setInitialValue(initialValue + rowsPerPage);
          }
        }}
      />
    );
  };

  return (
    <Box minH="100vh" bg={useColorModeValue("gray.50", "gray.900")} overflowX="hidden"  className="box-with-scrollbar" >
      {/* ✅ Header with title and search bar */}
      <Card bg="#fff" rounded="md" p={4} mb={3}>
        <Flex
          justify="space-between"
          align="center"
          flexDir={{ base: "column", md: "row" }}
          gap={3}
        >
          <Text fontWeight="bold" fontSize={{ base: "md", md: "lg" }}>
            Credit Management Dashboard
          </Text>

          {/* ✅ Searchbar */}
          <InputGroup bg="#f4f5f7" borderRadius={5} w={["100%", "auto"]} maxW="sm">
            <Input
              placeholder="Search by customer name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <InputRightElement>
              <FiSearch />
            </InputRightElement>
          </InputGroup>
        </Flex>
      </Card>

      {/* ✅ Tabs */}
      <Tabs
        variant="soft-rounded"
        colorScheme="green"
        index={activeTab}
        onChange={(i) => {
          setActiveTab(i);
          setCurrentPage(1);
          setInitialValue(1);
        }}
        isFitted
      >
        <TabList flexWrap="wrap" gap={2} overflowX="auto">
          {["Applications", "Approved", "Rejected", "Commented"].map((label, i) => (
            <Tab
              key={i}
              fontWeight="medium"
              fontSize={{ base: "xs", md: "sm" }}
              px={{ base: 3, md: 4 }}
              py={{ base: 1, md: 2 }}
              _selected={{ color: "white", bg: primaryColor }}
              whiteSpace="nowrap"
            >
              {label}
            </Tab>
          ))}
        </TabList>

        <TabPanels mt={4}>
          <TabPanel p={0}>
            {loading ? (
             <GasCylinderLoader size="sm" loadingText="Loading Credit applications"/>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading data: {error}
              </Alert>
            ) : (
              renderStatusTable(underReviewApplications, "applications")
            )}
          </TabPanel>

          <TabPanel p={0}>
            {loading ? (
              <GasCylinderLoader size="sm" loadingText="Loading Credit applications"/>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading data: {error}
              </Alert>
            ) : (
              renderStatusTable(approvedApplications, "approved")
            )}
          </TabPanel>

          <TabPanel p={0}>
            {loading ? (
              <GasCylinderLoader size="sm" loadingText="Loading Credit applications"/>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading data: {error}
              </Alert>
            ) : (
              renderStatusTable(rejectedApplications, "rejected")
            )}
          </TabPanel>

          <TabPanel p={0}>
            {loading ? (
              <GasCylinderLoader size="sm" loadingText="Loading Credit applications"/>
            ) : error ? (
              <Alert status="error" my={4}>
                <AlertIcon />
                Error loading data: {error}
              </Alert>
            ) : (
              renderStatusTable(commentedApplications, "commented")
            )}
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
}
